import { useQueryClient } from '@tanstack/react-query';

import type { CreateClientOnboardingArgs } from '../../api/types/user.types';

import { createOnboardingUser } from '../../api';
import { useAuthContext } from '../../context';
import { queryKeys } from '../../queryKeys';
import { useMutation } from '../base';

export default function useCreateOnboardingUser() {
  const queryClient = useQueryClient();
  const { refetchCurrentUser } = useAuthContext();
  const { error, mutateAsync, status } = useMutation<
    void,
    CreateClientOnboardingArgs,
    CreateClientOnboardingArgs
  >({
    mutationFn: async (args: CreateClientOnboardingArgs) => {
      await createOnboardingUser(args);
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.users.fetchCurrentUser._def,
      });
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.users.fetchInfiniteUsers._def,
      });
      // Trainer/organization onboarding creates the user's FIRST organization.
      // The switcher list was fetched (empty) during onboarding and, with the
      // global 2-min staleTime + refetchOnWindowFocus disabled, would otherwise
      // stay empty — leaving OrganizationProvider's auto-select effect stuck in
      // the org-less personal profile. Refresh it so the new org is picked up
      // as the active context (mirrors clearOrganizationCache in the switcher).
      await queryClient.invalidateQueries({
        queryKey:
          queryKeys.organizations.fetchInfiniteSwitcherOrganizations._def,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.fetchOrganizations._def,
      });
      // The authenticated user is NOT one of those caches: it is plain state in
      // `AuthProvider`, so the `users.fetchCurrentUser` invalidation above
      // refreshes nothing — no observer subscribes to that key. Onboarding just
      // created this account's first organization, role and membership, and
      // every org-scoped gate is decided from that snapshot: the sidebar's
      // permissions, and OrganizationGuard's membership check, which bounced
      // people out of the organization they had just created. Ask for it
      // explicitly, the way the "Add profile" path already does.
      await refetchCurrentUser();
    },
  });

  return {
    error,
    isLoading: status === 'pending',
    mutateAsync,
  };
}
